import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Plus, Trash2 } from "lucide-react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import DataTable from "../components/common/DataTable";
import { erpApi, formatDate } from "../services/erpApi";

const emptyMedicine = { name: "", dosage: "", frequency: "", duration: "", instructions: "" };
const emptyForm = {
  patient_id: "",
  doctor_name: "",
  symptoms: "",
  diagnosis: "",
  medicines: [{ ...emptyMedicine }],
  advice: "",
  tests_advised: "",
  follow_up_date: "",
  notes: "",
};

export default function Prescriptions() {
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [patientRes, prescriptionRes, doctorRes] = await Promise.all([erpApi.patients(), erpApi.prescriptions(), erpApi.doctors({ active: true })]);
    setPatients(patientRes.data);
    setPrescriptions(prescriptionRes.data);
    setDoctors(doctorRes.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Failed to load prescriptions"));
  }, []);

  const setMedicine = (index, key, value) => {
    const medicines = [...form.medicines];
    medicines[index] = { ...medicines[index], [key]: value };
    setForm({ ...form, medicines });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.patient_id || !form.doctor_name) {
      toast.error("Patient and doctor name are required");
      return;
    }
    const payload = {
      ...form,
      medicines: form.medicines.filter((medicine) => medicine.name.trim()),
    };
    try {
      await erpApi.createPrescription(payload);
      toast.success("Prescription saved");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save prescription");
    }
  };

  const downloadPdf = async (prescription) => {
    const response = await API.get(`/prescriptions/${prescription.id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `prescription_${prescription.id.slice(0, 8)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const deletePrescription = async (prescription) => {
    const ok = window.confirm(`Delete prescription for ${prescription.patient_name}?`);
    if (!ok) return;
    await erpApi.deletePrescription(prescription.id);
    toast.success("Prescription deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row">
        <div>
          <h1 className="font-manrope text-2xl font-bold">Prescriptions</h1>
          <p className="text-sm text-hospital-text-muted">Doctor prescriptions with medicines, advice, tests and PDF print.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary"><Plus className="mr-2 h-4 w-4" />New Prescription</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
            <DialogHeader><DialogTitle>Create Prescription</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Patient</Label>
                <Select value={form.patient_id} onValueChange={(value) => setForm({ ...form, patient_id: value })}>
                  <SelectTrigger><SelectValue placeholder="Select patient" /></SelectTrigger>
                  <SelectContent>{patients.map((patient) => <SelectItem key={patient.id} value={patient.id}>{patient.name} - {patient.mobile}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Doctor Name</Label>
                <Select value={form.doctor_name} onValueChange={(value) => setForm({ ...form, doctor_name: value })}>
                  <SelectTrigger><SelectValue placeholder="Select doctor" /></SelectTrigger>
                  <SelectContent>
                    {doctors.map((doctor) => <SelectItem key={doctor.id} value={doctor.name}>{doctor.name} - {doctor.speciality}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Symptoms</Label><Textarea value={form.symptoms} onChange={(e) => setForm({ ...form, symptoms: e.target.value })} /></div>
              <div className="space-y-2"><Label>Diagnosis</Label><Textarea value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} /></div>

              <Card className="border border-gray-200 md:col-span-2">
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-center justify-between">
                    <Label>Medicines</Label>
                    <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, medicines: [...form.medicines, { ...emptyMedicine }] })}>Add Medicine</Button>
                  </div>
                  {form.medicines.map((medicine, index) => (
                    <div key={index} className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-3 md:grid-cols-5">
                      <Input placeholder="Medicine" value={medicine.name} onChange={(e) => setMedicine(index, "name", e.target.value)} />
                      <Input placeholder="Dosage" value={medicine.dosage} onChange={(e) => setMedicine(index, "dosage", e.target.value)} />
                      <Input placeholder="Frequency" value={medicine.frequency} onChange={(e) => setMedicine(index, "frequency", e.target.value)} />
                      <Input placeholder="Duration" value={medicine.duration} onChange={(e) => setMedicine(index, "duration", e.target.value)} />
                      <Input placeholder="Instructions" value={medicine.instructions} onChange={(e) => setMedicine(index, "instructions", e.target.value)} />
                    </div>
                  ))}
                </CardContent>
              </Card>

              <div className="space-y-2"><Label>Tests Advised</Label><Textarea value={form.tests_advised} onChange={(e) => setForm({ ...form, tests_advised: e.target.value })} /></div>
              <div className="space-y-2"><Label>Advice</Label><Textarea value={form.advice} onChange={(e) => setForm({ ...form, advice: e.target.value })} /></div>
              <div className="space-y-2"><Label>Follow-up Date</Label><Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="flex justify-end gap-2 md:col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="btn-primary">Save Prescription</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable
        title="Prescription History"
        rows={prescriptions}
        empty="No prescriptions"
        columns={[
          { key: "created_at", label: "Date", render: (row) => formatDate(row.created_at) },
          { key: "patient_name", label: "Patient" },
          { key: "doctor_name", label: "Doctor" },
          { key: "diagnosis", label: "Diagnosis", render: (row) => row.diagnosis || "-" },
          { key: "medicines", label: "Medicines", render: (row) => `${row.medicines?.length || 0} item(s)` },
          { key: "actions", label: "Actions", render: (row) => <div className="flex gap-1"><Button variant="ghost" size="icon" title="Download PDF" onClick={() => downloadPdf(row)}><Download className="h-4 w-4 text-hospital-primary" /></Button><Button variant="ghost" size="icon" title="Delete" onClick={() => deletePrescription(row)}><Trash2 className="h-4 w-4 text-red-600" /></Button></div> },
        ]}
      />
    </div>
  );
}
